import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@/shared/api';
import { useAuthActions } from '../model/use-auth-actions';

const schema = z.object({
  // Keys, not sentences: the schema is built before anything is on screen and cannot ask
  // i18next for words. The component resolves them.
  email: z.string().min(1, 'auth.emailRequired').email('auth.emailInvalid'),
  password: z.string().min(8, 'auth.passwordShort'),
});

type FormValues = z.infer<typeof schema>;

export function LoginForm() {
  const { t } = useTranslation();
  const { login } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  const onSubmit = async (values: FormValues) => {
    setFormError(null);

    try {
      await login(values);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.errors.length > 0
          ? error.errors.join(' ')
          : t('auth.signInFailed'),
      );
    }
  };

  return (
    <Stack component="form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant="h5">{t('auth.signIn')}</Typography>

      {formError && <Alert severity="error">{formError}</Alert>}

      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        error={Boolean(errors.email)}
        helperText={errors.email?.message ? t(errors.email.message) : undefined}
        {...register('email')}
      />

      <TextField
        label={t('auth.password')}
        type="password"
        autoComplete="current-password"
        error={Boolean(errors.password)}
        helperText={errors.password?.message ? t(errors.password.message) : undefined}
        {...register('password')}
      />

      <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
        {t(isSubmitting ? 'auth.signingIn' : 'auth.signIn')}
      </Button>

      <Typography variant="body2" color="text.secondary">
        {t('auth.noAccountYet')}{' '}
        <Link component={RouterLink} to="/register">
          {t('auth.createOne')}
        </Link>
      </Typography>
    </Stack>
  );
}
